import { useState } from "react";
import { Loader2, Upload, Image, Video, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";

interface ChallengeSubmissionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  challenge: {
    id: string;
    title: string;
  };
  onSubmitted: () => void;
}

const ChallengeSubmissionDialog = ({
  open,
  onOpenChange,
  challenge,
  onSubmitted,
}: ChallengeSubmissionDialogProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("text");
  const [content, setContent] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10485760) {
        toast({
          title: "File too large",
          description: "Maximum image size is 10MB",
          variant: "destructive",
        });
        return;
      }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 52428800) {
        toast({
          title: "File too large",
          description: "Maximum video size is 50MB",
          variant: "destructive",
        });
        return;
      }
      setVideoFile(file);
    }
  };

  const handleSubmit = async () => {
    if (!user) return;

    const hasContent = content.trim() || imageFile || videoFile;
    if (!hasContent) {
      toast({
        title: "No content",
        description: "Please add some content to your submission",
        variant: "destructive",
      });
      return;
    }

    setSubmitting(true);
    try {
      let imageUrl: string | null = null;
      let videoUrl: string | null = null;

      // Upload image if provided
      if (imageFile) {
        const fileExt = imageFile.name.split('.').pop();
        const fileName = `${user.id}/${Date.now()}.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from('challenge-submissions')
          .upload(fileName, imageFile, {
            contentType: imageFile.type,
          });

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('challenge-submissions')
          .getPublicUrl(fileName);

        imageUrl = publicUrl;
      }

      // Upload video if provided
      if (videoFile) {
        const fileExt = videoFile.name.split('.').pop();
        const fileName = `${user.id}/${Date.now()}.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from('challenge-submissions')
          .upload(fileName, videoFile, {
            contentType: videoFile.type,
          });

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('challenge-submissions')
          .getPublicUrl(fileName);

        videoUrl = publicUrl;
      }

      // Create showcase post linked to challenge
      const { error: insertError } = await supabase
        .from('showcase_posts')
        .insert({
          user_id: user.id,
          challenge_id: challenge.id,
          content: content.trim() || null,
          image_url: imageUrl,
          video_url: videoUrl,
          title: `Challenge: ${challenge.title}`,
        });

      if (insertError) throw insertError;

      toast({
        title: "Submission posted!",
        description: "Your challenge entry is now visible in Showcase",
      });

      // Reset form
      setContent("");
      setImageFile(null);
      setVideoFile(null);
      setImagePreview(null);
      setActiveTab("text");
      onOpenChange(false);
      onSubmitted();
    } catch (error: any) {
      console.error('Error submitting:', error);
      toast({
        title: "Submission failed",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
        <DialogTitle className="text-lg">Submit to "{challenge.title}"</DialogTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Images: max 10MB • Videos: max 30 seconds, 50MB
          </p>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="text" className="gap-1">
              <FileText className="h-4 w-4" />
              Text
            </TabsTrigger>
            <TabsTrigger value="image" className="gap-1">
              <Image className="h-4 w-4" />
              Image
            </TabsTrigger>
            <TabsTrigger value="video" className="gap-1">
              <Video className="h-4 w-4" />
              Video
            </TabsTrigger>
          </TabsList>

          <TabsContent value="text" className="space-y-4 mt-4">
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Share your progress, thoughts, or achievements..."
              rows={5}
              maxLength={1000}
            />
            <p className="text-xs text-muted-foreground text-right">
              {content.length}/1000
            </p>
          </TabsContent>

          <TabsContent value="image" className="space-y-4 mt-4">
            <div>
              <Input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="mb-2"
              />
              <p className="text-xs text-muted-foreground">Max 10MB</p>
            </div>
            {imagePreview && (
              <img
                src={imagePreview}
                alt="Preview"
                className="w-full rounded-lg max-h-48 object-cover"
              />
            )}
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Add a caption (optional)..."
              rows={2}
              maxLength={500}
            />
          </TabsContent>

          <TabsContent value="video" className="space-y-4 mt-4">
            <div>
              <Input
                type="file"
                accept="video/mp4,video/quicktime,video/webm"
                onChange={handleVideoChange}
                className="mb-2"
              />
              <p className="text-xs text-muted-foreground">Max 30 seconds, 50MB</p>
            </div>
            {videoFile && (
              <p className="text-sm text-muted-foreground">
                Selected: {videoFile.name}
              </p>
            )}
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Add a caption (optional)..."
              rows={2}
              maxLength={500}
            />
          </TabsContent>
        </Tabs>

        <Button
          onClick={handleSubmit}
          disabled={submitting || (!content.trim() && !imageFile && !videoFile)}
          className="w-full mt-4"
        >
          {submitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <Upload className="mr-2 h-4 w-4" />
              Submit Entry
            </>
          )}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export default ChallengeSubmissionDialog;
